import React, { useState, useEffect } from "react";
import { HelpCircle, X, ChevronRight, ChevronLeft } from "lucide-react";

const HELP_SEEN_KEY = "taladraw_help_seen";

function getSeenScreens() {
  try {
    return JSON.parse(localStorage.getItem(HELP_SEEN_KEY) || "{}");
  } catch {
    return {};
  }
}

function markSeen(screen) {
  const seen = getSeenScreens();
  seen[screen] = true;
  localStorage.setItem(HELP_SEEN_KEY, JSON.stringify(seen));
}

export function HelpButton({ screen, tips, visible = true, position = "right" }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [tourMode, setTourMode] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const seen = getSeenScreens();
    if (!seen[screen]) {
      setOpen(true);
      setTourMode(true);
      setStep(0);
      markSeen(screen);
    }
  }, [screen, visible]);

  const handleOpen = () => {
    setOpen(true);
    setTourMode(false);
    setStep(0);
  };

  const handleClose = () => {
    setOpen(false);
    setStep(0);
  };

  const currentTip = tips[step];
  const isLast = step === tips.length - 1;
  const isFirst = step === 0;

  return (
    <>
      {/* Floating Help Button */}
      {visible && (
        <button
          onClick={handleOpen}
          className={`fixed bottom-24 z-[900] w-10 h-10 rounded-full bg-[#00FF94] text-black flex items-center justify-center shadow-lg shadow-[#00FF94]/20 hover:scale-110 transition-transform ${position === "left" ? "left-4" : "right-4"}`}
          data-testid="help-button"
        >
          <HelpCircle className="w-5 h-5" strokeWidth={2} />
        </button>
      )}

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-[950] flex items-end justify-center" data-testid="help-overlay">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

          {/* Content */}
          <div className="relative w-full max-w-md mx-4 mb-4 bg-[#131316] border border-zinc-800 rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4" data-testid="help-content">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-[#00FF94]" strokeWidth={2} />
                <span className="text-sm font-bold text-white">
                  {tourMode ? "Quick Tour" : "Help"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-zinc-500 font-mono">{step + 1} / {tips.length}</span>
                <button onClick={handleClose} className="p-1 text-zinc-500 hover:text-white transition-colors" data-testid="help-close">
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            </div>

            {/* Tip Content */}
            <div className="px-5 py-4 min-h-[120px]">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#00FF94]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-[#00FF94]">{currentTip.icon || (step + 1)}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white mb-1.5">{currentTip.title}</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">{currentTip.body}</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between px-5 pb-4">
              <button
                onClick={() => setStep(s => s - 1)}
                disabled={isFirst}
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white disabled:opacity-0 transition-all"
                data-testid="help-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back
              </button>

              {isLast ? (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-full bg-[#00FF94] text-black text-xs font-bold hover:bg-[#00FF94]/90 transition-colors"
                  data-testid="help-done"
                >
                  Got it!
                </button>
              ) : (
                <button
                  onClick={() => setStep(s => s + 1)}
                  className="flex items-center gap-1 px-4 py-2 rounded-full bg-zinc-800 text-white text-xs font-semibold hover:bg-zinc-700 transition-colors"
                  data-testid="help-next"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              )}
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 pb-3">
              {tips.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? "bg-[#00FF94]" : "bg-zinc-700"}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
